import React, { useState, useEffect } from 'react';
import {
  View,
  StyleSheet,
  ImageBackground,
  Dimensions,
  Platform,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';

interface VertivContainerProps {
  children: React.ReactNode;
}

export const VertivContainer: React.FC<VertivContainerProps> = ({ children }) => {
  const [screenData, setScreenData] = useState(Dimensions.get('window'));
  const isWeb = Platform.OS === 'web';
  const isLargeScreen = isWeb && screenData.width >= 1024;

  useEffect(() => {
    if (isWeb) {
      const subscription = Dimensions.addEventListener('change', ({ window }) => {
        setScreenData(window);
      });
      
      return () => subscription?.remove();
    }
  }, [isWeb]);
  
  return (
    <SafeAreaView style={styles.container}>
      <View style={[
        styles.content, 
        isWeb && { minHeight: screenData.height },
        isLargeScreen ? styles.horizontalLayout : styles.verticalLayout
      ]}>
        <View style={[
          styles.imageSection,
          isLargeScreen ? styles.imageSectionHorizontal : styles.imageSectionVertical
        ]}>
          <ImageBackground
            source={require('../assets/images/datacenter-background.png')}
            style={styles.backgroundImage}
            resizeMode="cover"
          >
            <View style={styles.overlay} />
          </ImageBackground>
        </View>
        {isLargeScreen ? (
          <View style={styles.contentSectionHorizontal}>
            <View style={styles.contentCard}>
              {children}
            </View>
          </View>
        ) : (
          <View style={styles.contentSectionVertical}>
            <View style={styles.contentCardVertical}>
              {children}
            </View>
          </View>
        )}
      </View>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#ffffff',
    overflow: 'visible',
  },
  content: {
    flex: 1,
    overflow: 'visible',
  },
  horizontalLayout: {
    flexDirection: 'row',
  },
  verticalLayout: {
    flexDirection: 'column',
    ...Platform.select({
      web: {
        position: 'relative',
      },
    }),
  },
  imageSection: {
    backgroundColor: '#f0f0f0',
  },
  imageSectionHorizontal: {
    flex: 1,
  },
  imageSectionVertical: {
    height: 300,
    ...Platform.select({
      web: {
        height: '40vh',
        minHeight: 250,
      },
    }),
  },
  backgroundImage: {
    flex: 1,
    width: '100%',
    height: '100%',
  },
  overlay: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: 'rgba(0, 0, 0, 0.3)',
  },
  contentSection: {
    backgroundColor: '#ffffff',
    alignItems: 'center',
    justifyContent: 'center',
  },
  contentSectionHorizontal: {
    flex: 1,
    paddingHorizontal: 60,
    paddingVertical: 40,
    overflow: 'visible',
  },
  contentSectionVertical: {
    paddingHorizontal: 24,
    zIndex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    overflow: 'visible',
    ...Platform.select({
      ios: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        paddingVertical: 40,
      },
      android: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        paddingVertical: 30,
      },
      web: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        paddingHorizontal: 32,
        paddingVertical: 40,
      },
    }),
  },
  contentCard: {
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'visible',
  },
  contentCardVertical: {
    backgroundColor: '#ffffff',
    borderRadius: 16,
    paddingHorizontal: 24,
    paddingVertical: 32,
    marginHorizontal: 0,
    maxWidth: 400,
    width: '100%',
    overflow: 'visible',
    ...Platform.select({
      ios: {
        paddingVertical: 40,
        shadowColor: '#000',
        shadowOffset: {
          width: 0,
          height: 4,
        },
        shadowOpacity: 0.1,
        shadowRadius: 12,
      },
      android: {
        paddingVertical: 36,
        elevation: 8,
      },
      web: {
        boxShadow: '0 4px 24px rgba(0, 0, 0, 0.1)',
        paddingHorizontal: 32,
        paddingVertical: 40,
      },
    }),
  },
});